/**
 * 第三方接入 —— 涂鸦云（live / mock 双模）与米家（mock）账号绑定、同步、编辑、删除。
 *
 * 同步回来的设备写进「我的设备」（source = tuya | mihome）且默认未认领：
 * 在设备墙「未认领」筛选里认领后，才可被控制台与语音助手（MCP）控制。
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ThirdpartyProvider } from '@znjj/shared';
import { deviceKeys } from '../api/devices';
import {
  integrationKeys,
  integrationsApi,
  type IntegrationAccount,
  type IntegrationCredentials,
} from '../api/integrations';
import { Icon } from '../components/Icon';
import { EmptyState, fmt, Sheet, toast } from '../components/ui';

interface EditorState {
  provider: ThirdpartyProvider;
  account: IntegrationAccount | null;
}

const PROVIDERS: { provider: ThirdpartyProvider; name: string; intro: string }[] = [
  {
    provider: 'tuya',
    name: '涂鸦云',
    intro:
      '未配置 AccessID 时走 mock 适配器；配置后走 /v1.0/token 真实链路（access_token 2 小时自动刷新）。',
  },
  {
    provider: 'mihome',
    name: '米家',
    intro: '官方云对接方向与平台相反且需开发者资质：第一版仅保存 token 占位 + mock 同步。',
  },
];

export function IntegrationsPage() {
  const qc = useQueryClient();
  const listQuery = useQuery({ queryKey: integrationKeys.list, queryFn: integrationsApi.list });
  const accounts = listQuery.data?.list ?? [];
  const tuyaPlatform = listQuery.data?.platform.tuya;

  const [editor, setEditor] = useState<EditorState | null>(null);

  /** 同步会改设备墙（新增未认领设备），故两处缓存一起刷新 */
  const refresh = (): void => {
    void qc.invalidateQueries({ queryKey: integrationKeys.list });
    void qc.invalidateQueries({ queryKey: deviceKeys.list });
  };

  const sync = useMutation({
    mutationFn: (id: string) => integrationsApi.sync(id),
    onSuccess: (res) => {
      refresh();
      toast.ok(`同步完成，「${res.account.label}」共 ${res.account.deviceCount} 台设备`);
    },
    onError: (err: Error) => toast.err(err.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => integrationsApi.remove(id),
    onSuccess: () => {
      refresh();
      toast.ok('账号已删除，由它同步进来的设备一并移除');
    },
    onError: (err: Error) => toast.err(err.message),
  });

  return (
    <>
      <div className="page-head">
        <h1 className="page-title">第三方接入</h1>
        <p className="page-sub">把涂鸦云 / 米家账号里的设备同步进来，统一在设备墙与语音助手里控制。</p>
      </div>

      <div className="card card--flat" style={{ marginBottom: 12 }}>
        <p className="card__desc">
          同步回来的设备来源标记为 <strong>涂鸦云 / 米家</strong>，并默认处于<strong>未认领</strong>状态：
          在设备墙「未认领」筛选里认领之后，才可以被控制台与语音助手（MCP）控制。
        </p>
      </div>

      {listQuery.isPending ? (
        <div className="stack" style={{ gap: 12 }}>
          <div className="skeleton skeleton--card" />
        </div>
      ) : (
        PROVIDERS.map(({ provider, name, intro }) => {
          const account = accounts.find((a) => a.provider === provider) ?? null;
          const syncing = sync.isPending && sync.variables === account?.id;
          return (
            <div className="card" key={provider} style={{ marginBottom: 12 }}>
              <div className="card__head">
                <div className="card__title">
                  <Icon name="cloud" size={16} />
                  {name}
                </div>
                <span
                  className={
                    'tag ' + (!account ? 'tag--off' : account.mode === 'live' ? 'tag--on' : 'tag--plain')
                  }
                >
                  {!account ? '未接入' : account.mode === 'live' ? '真实链路' : 'mock 模式'}
                </span>
              </div>

              {account ? (
                <>
                  <div className="row row--between row--wrap" style={{ gap: 10 }}>
                    <div className="stack" style={{ gap: 4, minWidth: 0 }}>
                      <span className="t-sm">{account.label}</span>
                      <span className="t-xs dim">
                        已同步 {account.deviceCount} 台设备 · 最近同步{' '}
                        {account.lastSyncAt ? fmt.dateTime(account.lastSyncAt) : '—'}
                        {account.mode === 'live' && account.nextRefreshAt
                          ? ` · 令牌刷新 ${fmt.dateTime(account.nextRefreshAt)}`
                          : ''}
                      </span>
                    </div>
                    <div className="btn-row">
                      <button
                        className="btn btn--sm btn--primary"
                        type="button"
                        disabled={syncing}
                        onClick={() => sync.mutate(account.id)}
                      >
                        <Icon name="refresh-cw" size={14} />
                        {syncing ? '同步中…' : '同步'}
                      </button>
                      <button
                        className="btn btn--sm btn--ghost"
                        type="button"
                        onClick={() => setEditor({ provider, account })}
                      >
                        <Icon name="pencil" size={14} />
                        编辑
                      </button>
                      <button
                        className="btn btn--sm btn--ghost"
                        type="button"
                        disabled={remove.isPending}
                        onClick={() => {
                          if (confirm(`删除「${account.label}」并移除其同步进来的设备？`)) {
                            remove.mutate(account.id);
                          }
                        }}
                      >
                        <Icon name="trash-2" size={14} />
                        删除
                      </button>
                    </div>
                  </div>

                  {account.status === 'error' ? (
                    <div className="row" style={{ marginTop: 10, alignItems: 'flex-start' }}>
                      <Icon
                        name="alert-triangle"
                        size={14}
                        style={{ color: 'var(--color-warning)', marginTop: 3 }}
                      />
                      <span className="t-sm" style={{ color: 'var(--color-warning)', lineHeight: 1.6 }}>
                        上次同步失败：请检查凭据后重试（错误详情见后端日志）。
                      </span>
                    </div>
                  ) : null}

                  <p className="card__desc" style={{ marginTop: 8 }}>
                    {intro}
                  </p>
                </>
              ) : (
                <>
                  {provider === 'tuya' ? (
                    <p className="card__desc">
                      {tuyaPlatform?.configured
                        ? `平台已配置 AccessID（${tuyaPlatform.apiBase}），新增账号将走真实链路。`
                        : '平台未配置 AccessID（TUYA_ACCESS_ID/SECRET）。新增账号默认走 mock 适配器；也可以在账号里直接填 AccessID / AccessSecret 走真实链路。'}
                    </p>
                  ) : (
                    <p className="card__desc">{intro}</p>
                  )}
                  <div className="btn-row" style={{ marginTop: 12 }}>
                    <button
                      className="btn btn--primary"
                      type="button"
                      onClick={() => setEditor({ provider, account: null })}
                    >
                      <Icon name="plus" size={15} />
                      添加{name}账号
                    </button>
                  </div>
                </>
              )}
            </div>
          );
        })
      )}

      {listQuery.isSuccess && !accounts.length ? (
        <div className="card card--flat">
          <EmptyState
            icon="cloud"
            title="还没有接入任何第三方云"
            desc="添加涂鸦云或米家账号后点「同步」，云上设备就会出现在设备墙。"
          />
        </div>
      ) : null}

      <IntegrationSheet editor={editor} onClose={() => setEditor(null)} />
    </>
  );
}

/** 新增 / 编辑账号表单（凭据不回显：填写即替换，留空则保持原样） */
function IntegrationSheet({
  editor,
  onClose,
}: {
  editor: EditorState | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const account = editor?.account ?? null;
  const isTuya = editor?.provider === 'tuya';

  const [label, setLabel] = useState(account?.label ?? '');
  const [accessId, setAccessId] = useState('');
  const [accessSecret, setAccessSecret] = useState('');
  const [token, setToken] = useState('');

  const save = useMutation({
    mutationFn: () => {
      if (!editor) throw new Error('缺少平台信息');
      const filled = isTuya ? accessId.trim() || accessSecret.trim() : token.trim();
      const credentials: IntegrationCredentials | undefined = !filled
        ? undefined
        : isTuya
          ? { accessId: accessId.trim(), accessSecret: accessSecret.trim() }
          : { token: token.trim() };
      return account
        ? integrationsApi.update(account.id, {
            label,
            ...(credentials ? { credentials } : {}),
          })
        : integrationsApi.create({ provider: editor.provider, label, credentials: credentials ?? {} });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: integrationKeys.list });
      toast.ok(account ? '账号已更新，点「同步」拉取最新设备' : '账号已添加，点「同步」拉取云上设备');
      onClose();
    },
    onError: (err: Error) => toast.err(err.message),
  });

  return (
    <Sheet open={!!editor} onClose={onClose} title={account ? '编辑账号' : '添加第三方账号'}>
      <div className="field">
        <span className="field__label">账号备注</span>
        <input
          className="input"
          value={label}
          placeholder={isTuya ? '涂鸦云账号' : '米家账号'}
          onChange={(e) => setLabel(e.target.value)}
        />
      </div>

      {isTuya ? (
        <>
          <div className="field">
            <span className="field__label">AccessID</span>
            <input
              className="input"
              value={accessId}
              placeholder="涂鸦开放平台的 AccessID"
              onChange={(e) => setAccessId(e.target.value)}
            />
          </div>
          <div className="field">
            <span className="field__label">AccessSecret</span>
            <input
              className="input"
              value={accessSecret}
              placeholder="涂鸦开放平台的 AccessSecret"
              onChange={(e) => setAccessSecret(e.target.value)}
            />
            <span className="field__hint">
              两个都留空 = 使用平台配置或 mock 适配器；填写后该账号走 /v1.0/token 真实链路，
              拉取设备时会取回 access_token 并在 2 小时后自动刷新。
              {account ? ' 凭据不回显，留空表示保持不变。' : ''}
            </span>
          </div>
        </>
      ) : (
        <div className="field">
          <span className="field__label">米家接入 token</span>
          <input
            className="input"
            value={token}
            placeholder="粘贴米家 token（第一版仅占位保存 + mock 同步）"
            onChange={(e) => setToken(e.target.value)}
          />
          <span className="field__hint">
            官方云对接需资质且方向与平台相反，第一版不会真正调用小米云；token 仅作占位，
            同步得到的是 mock 设备。{account ? ' 留空表示保持不变。' : ''}
          </span>
        </div>
      )}

      <div className="btn-row" style={{ marginTop: 16 }}>
        <button
          className="btn btn--primary"
          type="button"
          disabled={save.isPending}
          onClick={() => save.mutate()}
        >
          <Icon name="check" size={15} />
          {save.isPending ? '保存中…' : '保存'}
        </button>
        <button className="btn btn--ghost" type="button" onClick={onClose}>
          取消
        </button>
      </div>
    </Sheet>
  );
}
